import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { PaginationInfoSchema } from './telephony';
const c = initContract();
export const UpdateTrackingNumberBodySchema = z.object({
    trackingNumber: z.string().nullable(),
});
/**
 * Item metadata, snapshotted when the item is added to the cart (site name,
 * identity, plan/operator labels, SIM details) — orders display without any
 * live resolution. Shapes mirror the persisted rows, hence the wide
 * nullability on SIM (reduced shapes for child SIMs and batches).
 */
export const LineOrderItemMetadataSchema = z.object({
    type: z.literal('LINE'),
    simId: z.string().nullish().default(null),
    planId: z.string(),
    siteId: z.string(),
    simType: z.enum(['physique', 'esim']),
    lastName: z.string().nullish().default(null),
    firstName: z.string().nullish().default(null),
    rioNumber: z.string().nullish().default(null),
    identityId: z.string().nullish().default(null),
    operatorId: z.string(),
    orderNature: z.enum(['creation', 'portability']),
    portedNumber: z.string().nullish().default(null),
    mobileLineType: z.enum(['classic', 'dataonly']),
    physiqueOption: z.enum(['order', 'stock']),
    /** ISO string on the wire. */
    portabilityDate: z.string().nullish().default(null),
    simNumber: z.string().nullish().default(null),
    siteName: z.string().nullish().default(null),
});
export const SimOrderItemMetadataSchema = z.object({
    type: z.literal('SIM'),
    simId: z.string().nullish().default(null),
    simType: z.enum(['physique', 'esim']),
    simNumber: z.string().nullish().default(null),
    orderNature: z.enum(['creation', 'portability']).nullish().default(null),
    portedNumber: z.string().nullish().default(null),
    physiqueOption: z.enum(['order', 'stock']).nullish().default(null),
    operatorId: z.string().nullish().default(null),
    operatorLabel: z.string().nullish().default(null),
    siteId: z.string().nullish().default(null),
    siteName: z.string().nullish().default(null),
});
export const DeviceOrderItemMetadataSchema = z.object({
    type: z.literal('DEVICE'),
    deviceId: z.string(),
    category: z.string().nullish().default(null),
    brand: z.string().nullish().default(null),
    model: z.string().nullish().default(null),
    condition: z.enum(['NEW', 'USED']).nullish().default(null),
    color: z.string().nullish().default(null),
    capacity: z.number().nullish().default(null),
    imageUrl: z.string().nullish().default(null),
    deviceCapacity: z.number().nullish().default(null),
    deviceColor: z.string().nullish().default(null),
    siteId: z.string().nullish().default(null),
    siteName: z.string().nullish().default(null),
});
export const AccessoryOrderItemMetadataSchema = z.object({
    type: z.literal('ACCESSORY'),
    accessoryId: z.string(),
    category: z.string().nullish().default(null),
    name: z.string().nullish().default(null),
    imageUrl: z.string().nullish().default(null),
    compatibleDeviceBrand: z.string().nullish().default(null),
    compatibleDeviceModel: z.string().nullish().default(null),
    color: z.string().nullish().default(null),
    siteId: z.string().nullish().default(null),
    siteName: z.string().nullish().default(null),
});
/** Who receives the QR code of a travel eSIM: a fleet line holder or an outsider. */
export const TravelEsimBeneficiarySchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('LINE'),
        lineId: z.string(),
        lineNumber: z.string(),
        name: z.string(),
        email: z.string(),
    }),
    z.object({
        kind: z.literal('EXTERNAL'),
        name: z.string(),
        email: z.string(),
    }),
]);
/** A grid plan Eiko has to buy `quantity` times for this eSIM (internal). */
export const TravelEsimComponentSchema = z.object({
    tariffId: z.string(),
    planLabel: z.string(),
    durationDays: z.number().int(),
    priceCents: z.number().int(),
    quantity: z.number().int(),
});
/**
 * Snapshot of the quote and the stay taken when the item was added to the
 * cart: the order never depends on the (versioned) grid afterwards. The
 * client sees one plan (data, stay, total, per day); `components` is what
 * Eiko buys and is only shown to Octave admins.
 */
export const TRAVEL_ESIM_ORDER_KINDS = ['CREATION', 'RECHARGE'];
export const TravelEsimOrderKindSchema = z.enum(TRAVEL_ESIM_ORDER_KINDS);
export const TravelEsimOrderItemMetadataSchema = z.object({
    type: z.literal('TRAVEL_ESIM'),
    country: z.string(),
    countryEn: z.string().nullish().default(null),
    planLabel: z.string(),
    /** Data for the whole stay in GB (chained plans add up), null = unlimited. */
    dataLimitGb: z.number().nullable(),
    departureDate: z.string(),
    returnDate: z.string(),
    stayDurationDays: z.number().int(),
    coveredDays: z.number().int(),
    totalPriceCents: z.number().int(),
    pricePerDayCents: z.number().int(),
    components: z.array(TravelEsimComponentSchema),
    beneficiary: TravelEsimBeneficiarySchema,
    siteId: z.string(),
    siteName: z.string().nullish().default(null),
    /** Storage path of the QR code attached by Octave. */
    qrCodePath: z.string().nullish().default(null),
    /** ISO date of the last e-mail of the QR code to the beneficiary. */
    qrCodeSentAt: z.string().nullish().default(null),
    /** Number of the eSIM issued by the provider, entered by Octave; lets it be recharged later. */
    esimNumber: z.string().nullish().default(null),
    /** First plan of a new eSIM, or a recharge of an existing one (lines before this field are creations). */
    orderKind: TravelEsimOrderKindSchema.catch('CREATION'),
    /** The beneficiary's eSIM the plan is loaded on. */
    travelEsimId: z.string().nullish().default(null),
    /** Recharge only: when Octave confirmed the plan was loaded on the eSIM. */
    planLoadedAt: z.string().nullish().default(null),
});
export const SetTravelEsimNumberBodySchema = z.object({
    esimNumber: z.string().trim().min(5).max(64),
});
export const FeeOrderItemMetadataSchema = z.object({
    type: z.literal('FEES'),
    /** Billing breakdown per billing centre (amounts in cents). */
    allocations: z
        .array(z.object({
        siteId: z.string().nullable(),
        siteName: z.string().nullish().default(null),
        amount: z.number(),
    }))
        .nullish()
        .default(null),
});
export const OrderItemMetadataSchema = z.discriminatedUnion('type', [
    LineOrderItemMetadataSchema,
    SimOrderItemMetadataSchema,
    DeviceOrderItemMetadataSchema,
    AccessoryOrderItemMetadataSchema,
    TravelEsimOrderItemMetadataSchema,
    FeeOrderItemMetadataSchema,
]);
export const OrderItemSchema = z.object({
    id: z.string(),
    type: z.enum([
        'LINE',
        'SIM',
        'ESIM',
        'DEVICE',
        'ACCESSORY',
        'FEES',
        'TRAVEL_ESIM',
    ]),
    name: z.string(),
    description: z.string().nullable(),
    price: z.number(),
    quantity: z.number(),
    total: z.number(),
    isRecurring: z.boolean(),
    isFixed: z.boolean(),
    metadata: OrderItemMetadataSchema,
});
export const OrderDeliveryAddressSchema = z.object({
    street: z.string(),
    zipCode: z.string(),
    city: z.string(),
    country: z.string(),
    isProfessional: z.boolean(),
    companyName: z.string().nullish().default(null),
    recipientName: z.string().nullish().default(null),
});
export const OrderSchema = z.object({
    id: z.string(),
    orderNumber: z.string(),
    status: z.string(),
    /** ISO string on the wire. */
    createdAt: z.string(),
    createdBy: z
        .object({
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
    })
        .nullable(),
    organization: z.object({ id: z.string(), name: z.string() }),
    items: z.array(OrderItemSchema),
    total: z.number(),
    fixedTotal: z.number(),
    recurringTotal: z.number(),
    deliveryAddress: OrderDeliveryAddressSchema.nullable(),
    shippingMethod: z.string().nullable(),
    shippingCost: z.number().nullable(),
    trackingNumber: z.string().nullable(),
});
/** A travel eSIM ordered for a fleet line, as listed in the line detail. */
export const LineTravelEsimSchema = z.object({
    orderId: z.string(),
    orderNumber: z.string(),
    orderStatus: z.string(),
    orderCreatedAt: z.string(),
    item: OrderItemSchema,
});
const coerceToStringArray = z
    .union([z.string(), z.array(z.string())])
    .transform((v) => (Array.isArray(v) ? v : [v]));
export const TRAVEL_ESIM_QR_CODE_STATES = ['pending', 'sent'];
export const TravelEsimQrCodeStateSchema = z.enum(TRAVEL_ESIM_QR_CODE_STATES);
export const TRAVEL_ESIM_BENEFICIARY_KINDS = ['fleet', 'external'];
export const TravelEsimBeneficiaryKindSchema = z.enum(TRAVEL_ESIM_BENEFICIARY_KINDS);
/** Where the stay stands today: departure in the future, in progress, or returned. */
export const TRAVEL_ESIM_STAY_STATES = ['upcoming', 'ongoing', 'past'];
export const TravelEsimStayStateSchema = z.enum(TRAVEL_ESIM_STAY_STATES);
export const ListOrganizationTravelEsimsQuerySchema = z.object({
    /** Matches destination, beneficiary name, e-mail, line number or order number. */
    q: z.string().optional(),
    qrCode: coerceToStringArray
        .pipe(z.array(TravelEsimQrCodeStateSchema))
        .optional(),
    beneficiary: coerceToStringArray
        .pipe(z.array(TravelEsimBeneficiaryKindSchema))
        .optional(),
    stay: coerceToStringArray
        .pipe(z.array(TravelEsimStayStateSchema))
        .optional(),
    page: z.coerce.number().int().positive().default(1),
    limit: z.coerce.number().int().positive().max(100).default(20),
});
export const OrganizationTravelEsimsSummarySchema = z.object({
    total: z.number().int(),
    /** Stays in progress today. */
    ongoing: z.number().int(),
    /** QR code not sent to the beneficiary yet. */
    awaitingQrCode: z.number().int(),
});
/** The stay the eSIM is on, or about to be on, or the last one. */
export const TravelEsimStaySummarySchema = z.object({
    country: z.string(),
    departureDate: z.string(),
    returnDate: z.string(),
    state: TravelEsimStayStateSchema,
});
/** An international eSIM of the organization, as the list shows it. */
export const OrganizationTravelEsimSchema = z.object({
    id: z.string(),
    beneficiary: TravelEsimBeneficiarySchema,
    esimNumber: z.string().nullable(),
    qrCodeSentAt: z.string().nullable(),
    createdAt: z.string(),
    plansCount: z.number().int(),
    currentStay: TravelEsimStaySummarySchema.nullable(),
    /** Billing center of the latest plan. */
    siteId: z.string().nullable(),
    siteName: z.string().nullable(),
});
export const OrganizationTravelEsimsSchema = z.object({
    items: z.array(OrganizationTravelEsimSchema),
    pageInfo: PaginationInfoSchema,
    summary: OrganizationTravelEsimsSummarySchema,
});
/** An eSIM with every plan loaded on it, most recent departure first. */
export const TravelEsimDetailSchema = OrganizationTravelEsimSchema.extend({
    plans: z.array(LineTravelEsimSchema),
});
export const TravelEsimQrCodeUrlSchema = z.object({
    /** Short-lived signed URL of the QR code image. */
    url: z.string(),
    expiresIn: z.number().int(),
});
export const AttachTravelEsimQrCodeResultSchema = z.object({
    qrCodePath: z.string(),
});
export const SendTravelEsimQrCodeResultSchema = z.object({
    sentAt: z.string(),
});
export const orderContract = c.router({
    /**
     * Octave admin attaches the QR code image (generated on the provider's
     * tool) to a travel eSIM order line: FormData with a single `file` entry
     * (png, jpeg or webp), handled by the server interceptor. Stored only:
     * the e-mail to the beneficiary is a separate step (`sendTravelEsimQrCode`).
     */
    attachTravelEsimQrCode: {
        method: 'POST',
        path: '/admin/orders/:id/items/:itemId/travel-esim-qr-code',
        pathParams: z.object({
            id: z.string().min(1),
            itemId: z.string().min(1),
        }),
        contentType: 'multipart/form-data',
        body: z.object({}),
        responses: { 201: AttachTravelEsimQrCodeResultSchema },
        summary: 'Attach the QR code of a travel eSIM order line (admin)',
    },
    /** E-mails the attached QR code to the beneficiary (again, if already sent). */
    sendTravelEsimQrCode: {
        method: 'POST',
        path: '/admin/orders/:id/items/:itemId/travel-esim-qr-code/send',
        pathParams: z.object({
            id: z.string().min(1),
            itemId: z.string().min(1),
        }),
        body: c.noBody(),
        responses: { 201: SendTravelEsimQrCodeResultSchema },
        summary: 'Send the QR code of a travel eSIM order line to its beneficiary (admin)',
    },
    /** Octave admin records the provider's eSIM number on the line (recharges later). */
    setTravelEsimNumber: {
        method: 'PATCH',
        path: '/admin/orders/:id/items/:itemId/travel-esim-number',
        pathParams: z.object({
            id: z.string().min(1),
            itemId: z.string().min(1),
        }),
        body: SetTravelEsimNumberBodySchema,
        responses: { 200: z.object({ success: z.literal(true) }) },
        summary: 'Set the eSIM number of a travel eSIM order line (admin)',
    },
    markTravelEsimPlanLoaded: {
        method: 'PATCH',
        path: '/admin/orders/:id/items/:itemId/travel-esim-loaded',
        pathParams: z.object({
            id: z.string().min(1),
            itemId: z.string().min(1),
        }),
        body: c.noBody(),
        responses: { 200: z.object({ success: z.literal(true) }) },
        summary: 'Confirm a recharge plan was loaded on the eSIM (admin)',
    },
    getTravelEsimQrCodeUrl: {
        method: 'GET',
        path: '/orders/:id/items/:itemId/travel-esim-qr-code',
        pathParams: z.object({
            id: z.string().min(1),
            itemId: z.string().min(1),
        }),
        responses: { 200: TravelEsimQrCodeUrlSchema },
        summary: 'Signed URL of the QR code of a travel eSIM order line',
    },
    listLineTravelEsims: {
        method: 'GET',
        path: '/lines/:lineId/travel-esims',
        pathParams: z.object({ lineId: z.string().min(1) }),
        responses: { 200: z.array(TravelEsimDetailSchema) },
        summary: 'The eSIMs of a fleet line, each with its plans',
    },
    getTravelEsim: {
        method: 'GET',
        path: '/travel-esims/:id',
        pathParams: z.object({ id: z.string().min(1) }),
        responses: { 200: TravelEsimDetailSchema },
        summary: 'One eSIM with its plans (the id of an order line is accepted too)',
    },
    deleteTravelEsim: {
        method: 'DELETE',
        path: '/travel-esims/:id',
        pathParams: z.object({ id: z.string().min(1) }),
        body: c.noBody(),
        responses: { 200: z.object({ success: z.boolean() }) },
        summary: 'Delete an eSIM with its plans, and the orders left empty (admin)',
    },
    getTravelEsimQrCodeUrlById: {
        method: 'GET',
        path: '/travel-esims/:id/qr-code',
        pathParams: z.object({ id: z.string().min(1) }),
        responses: { 200: TravelEsimQrCodeUrlSchema },
        summary: 'Short-lived URL of the QR code of an eSIM',
    },
    resendTravelEsimQrCode: {
        method: 'POST',
        path: '/travel-esims/:id/resend',
        pathParams: z.object({ id: z.string().min(1) }),
        body: c.noBody(),
        responses: { 201: SendTravelEsimQrCodeResultSchema },
        summary: 'Send the QR code of an eSIM again to its beneficiary (organization member)',
    },
    listOrganizationTravelEsims: {
        method: 'GET',
        path: '/organizations/:organizationId/travel-esims',
        pathParams: z.object({ organizationId: z.string().min(1) }),
        query: ListOrganizationTravelEsimsQuerySchema,
        responses: { 200: OrganizationTravelEsimsSchema },
        summary: 'International eSIMs of an organization, one per eSIM, paginated',
    },
    listOrders: {
        method: 'GET',
        path: '/orders',
        responses: { 200: z.array(OrderSchema) },
        summary: 'All orders, all organizations (admin)',
    },
    listMyOrders: {
        method: 'GET',
        path: '/orders/me',
        query: z.object({ organizationId: z.string().min(1) }),
        responses: { 200: z.array(OrderSchema) },
        summary: 'Orders of an organization',
    },
    getOrder: {
        method: 'GET',
        path: '/orders/:id',
        pathParams: z.object({ id: z.string().min(1) }),
        responses: { 200: OrderSchema },
        summary: 'One order with its items and snapshots',
    },
    getPendingOrdersCount: {
        method: 'GET',
        path: '/orders/pending-count',
        responses: {
            200: z.object({ count: z.number() }),
        },
        summary: 'Count pending orders (admin)',
    },
    completeOrder: {
        method: 'POST',
        path: '/orders/:id/complete',
        pathParams: z.object({ id: z.string().min(1) }),
        body: c.noBody(),
        responses: {
            201: z.object({ success: z.boolean() }),
        },
        summary: 'Mark an order as completed',
    },
    updateTrackingNumber: {
        method: 'PATCH',
        path: '/orders/:id/tracking-number',
        pathParams: z.object({ id: z.string().min(1) }),
        body: UpdateTrackingNumberBodySchema,
        responses: {
            200: z.object({ success: z.boolean() }),
        },
        summary: 'Set or clear the tracking number of an order (admin)',
    },
    deleteOrder: {
        method: 'DELETE',
        path: '/orders/:id',
        pathParams: z.object({ id: z.string().min(1) }),
        body: c.noBody(),
        responses: {
            200: z.object({ success: z.boolean() }),
        },
        summary: 'Delete an order',
    },
});
